import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ScheduledEvent } from "@tih/shared";
import { isRealEventPoster } from "@tih/shared";
import {
  ApiError,
  adminDeleteEvent,
  adminFetchEvents,
  adminFetchFeaturedEvent,
  adminFetchLive,
} from "../../../api/client";
import {
  compareAdminEvents,
  phaseBadgeClass,
  phaseLabel,
  rowPhase,
} from "../../../lib/event-admin-utils";
import { useSiteSettings } from "../../../context/SiteSettingsContext";

type ListFilter = "all" | "upcoming" | "past";

export function AdminEventsListPage() {
  const { refresh: refreshSite } = useSiteSettings();
  const [rows, setRows] = useState<ScheduledEvent[]>([]);
  const [featuredId, setFeaturedId] = useState<number | null>(null);
  const [onAirEventId, setOnAirEventId] = useState<number | null>(null);
  const [listFilter, setListFilter] = useState<ListFilter>("upcoming");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [events, featured, live] = await Promise.all([
        adminFetchEvents(),
        adminFetchFeaturedEvent(),
        adminFetchLive().catch(() => null),
      ]);
      setRows(events);
      setFeaturedId(featured?.id ?? null);
      const onAir =
        live?.info?.isLive
          ? (live.events.find((e) => e.streamRole === "current")?.id ?? null)
          : null;
      setOnAirEventId(onAir);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const filteredRows = useMemo(() => {
    const now = Date.now();
    let list = rows;
    if (listFilter === "upcoming") {
      list = rows.filter((ev) => rowPhase(ev, now) !== "past");
    } else if (listFilter === "past") {
      list = rows.filter((ev) => rowPhase(ev, now) === "past");
    }
    return [...list].sort((a, b) => compareAdminEvents(a, b, now));
  }, [rows, listFilter]);

  async function remove(id: number) {
    if (!confirm("Delete this scheduled event?")) return;
    setBusy(true);
    try {
      await adminDeleteEvent(id);
      await load();
      await refreshSite();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>Events</h1>
          <p>
            Manage worship overnights from one place — poster, stream, album, registration, RSVPs,
            and blog posts for each event.
          </p>
        </div>
        <Link to="/admin/events/new" className="btn btn-primary">
          + Schedule event
        </Link>
      </header>

      {error && <div className="form-error">{error}</div>}

      <div className="admin-gallery-filters" style={{ marginBottom: 16 }}>
        {(["all", "upcoming", "past"] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={`admin-gallery-filter${listFilter === f ? " admin-gallery-filter--active" : ""}`}
            onClick={() => setListFilter(f)}
          >
            {f === "all" ? "All events" : f === "upcoming" ? "Upcoming & live" : "Past events"}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div style={{ opacity: 0.6, marginBottom: 24 }}>
          {listFilter === "past"
            ? "No past events yet."
            : listFilter === "upcoming"
              ? "No upcoming events — schedule one to get started."
              : "No scheduled events yet."}
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Poster</th>
                <th>Title</th>
                <th>Starts</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((ev) => {
                const phase = rowPhase(ev);
                return (
                  <tr key={ev.id} className={phase === "past" ? "admin-table-row--past" : undefined}>
                    <td>
                      {isRealEventPoster(ev.posterUrl) ? (
                        <div className="admin-table-poster">
                          <img src={ev.posterUrl} alt="" loading="lazy" />
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <Link to={`/admin/events/${ev.id}/overview`} className="admin-event-list-link">
                        {ev.title ?? "Untitled event"}
                      </Link>
                    </td>
                    <td>{new Date(ev.startsAt).toLocaleString()}</td>
                    <td>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {ev.status === "CANCELLED" ? (
                          <span className="admin-badge admin-badge-muted">Cancelled</span>
                        ) : (
                          <span className={phaseBadgeClass(phase)}>{phaseLabel(phase)}</span>
                        )}
                        {featuredId === ev.id && (
                          <span className="admin-badge admin-badge-featured">On public home</span>
                        )}
                        {onAirEventId === ev.id && (
                          <span className="admin-badge admin-badge-live">On air</span>
                        )}
                      </div>
                    </td>
                    <td className="admin-row-actions">
                      <Link to={`/admin/events/${ev.id}/overview`} className="btn btn-primary btn-sm">
                        Manage
                      </Link>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={busy}
                        onClick={() => void remove(ev.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
